import { NavLink, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { Home, Compass, Ticket, User, ScanLine } from "lucide-react";
import { Capacitor } from "@capacitor/core";

import Onboarding from "../components/Onboarding.jsx";
import { fetchVendorAccess } from "../utils/vendorAccess";
import "../styles/components.css";
import "../styles/theme.css";
import "../styles/main.css";
import "../styles/app.css";

export default function AppShell() {
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [isVendor, setIsVendor] = useState(false);

    useEffect(() => {
        let alive = true;

        const evaluateSession = async (nextSession = null) => {
            const session = nextSession ?? (await supabase.auth.getSession()).data?.session ?? null;
            if (!alive) return;

            const userId = session?.user?.id;
            if (!userId) {
                setShowOnboarding(false);
                setIsVendor(false);
                return;
            }

            const key = `bbp_onboarded_v1_${userId}`;
            if (!localStorage.getItem(key)) setShowOnboarding(true);

            const vendorState = await fetchVendorAccess();
            if (!alive) return;
            setIsVendor(Boolean(vendorState.isVendor));
        };

        evaluateSession();

        const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
            evaluateSession(session);
        });

        return () => {
            alive = false;
            sub?.subscription?.unsubscribe?.();
        };
    }, []);

    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            document.body.classList.add("bbp-native");
            document.documentElement.classList.add("bbp-native");
        }
        return () => {
            document.body.classList.remove("bbp-native");
            document.documentElement.classList.remove("bbp-native");
        };
    }, []);

    const closeOnboarding = async () => {
        const { data } = await supabase.auth.getSession();
        const userId = data?.session?.user?.id;
        if (userId) localStorage.setItem(`bbp_onboarded_v1_${userId}`, "1");
        setShowOnboarding(false);
    };

    return (
        <div className="app-shell">
            <main className="app-main">
                <Outlet />
            </main>

            {showOnboarding && <Onboarding onDone={closeOnboarding} />}

            <nav className="tabbar" aria-label="Bottom Navigation">
                <Tab to="/home" icon={<Home size={20} />} label="Home" />
                <Tab to="/discover" icon={<Compass size={20} />} label="Explore" />
                <Tab
                    to={isVendor ? "/vendor" : "/my-pass"}
                    icon={isVendor ? <ScanLine size={20} /> : <Ticket size={20} />}
                    label={isVendor ? "Vendor" : "My Pass"}
                />
                <Tab to="/account" icon={<User size={20} />} label="Account" />
            </nav>
        </div>
    );
}

function Tab({ to, icon, label }) {
    return (
        <NavLink to={to} className={({ isActive }) => `tab ${isActive ? "active" : ""}`}>
            <div className="tab-icon">{icon}</div>
            <div className="tab-label">{label}</div>
            <div className="tab-active-pill" />
        </NavLink>
    );
}
